import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, X, Send, Loader2, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackChatbotLead, trackChatbotBooking } from "@/lib/meta-pixel";
import { Link } from "react-router-dom";

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AIMentorChatbotProps {
  userRole?: string | null;
  isStudentDashboard?: boolean;
  onBookMentor?: (mentorId: string) => void;
}

export const AIMentorChatbot = ({ userRole, isStudentDashboard = false, onBookMentor }: AIMentorChatbotProps) => {
  // Guard: hide chatbot for mentors/admins BEFORE any hooks to keep hook order stable
  if (userRole === 'mentor' || userRole === 'admin') {
    return null;
  }

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPsstNotification, setShowPsstNotification] = useState(false);
  const [remainingMessages, setRemainingMessages] = useState<number | null>(null);
  const [lastMessageTime, setLastMessageTime] = useState<number>(0);
  const [canSendMessage, setCanSendMessage] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const DAILY_LIMIT_AUTH = 10; // Authenticated users get 10 messages/day
  const COOLDOWN_MS = 3000; // 3 second cooldown between messages

  // Check authentication and initialize usage tracking
  useEffect(() => {
    const checkAuthAndUsage = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        setIsAuthenticated(true);
        setUserId(user.id);
        
        // Check database usage
        const { data, error } = await supabase
          .from('ai_chat_usage')
          .select('message_count, last_reset_date')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (!error && data) {
          const today = new Date().toISOString().split('T')[0];
          const lastReset = new Date(data.last_reset_date).toISOString().split('T')[0];
          
          if (today === lastReset) {
            setRemainingMessages(DAILY_LIMIT_AUTH - data.message_count);
          } else {
            setRemainingMessages(DAILY_LIMIT_AUTH);
          }
        } else {
          setRemainingMessages(DAILY_LIMIT_AUTH);
        }
      } else {
        setIsAuthenticated(false);
      }
    };

    checkAuthAndUsage();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Periodic "Psst!" notification to draw attention to the chatbot
  useEffect(() => {
    // Only show for students who haven't opened the chat yet
    if (isOpen) return;

    const showNotification = () => {
      setShowPsstNotification(true);
      // Hide after 3 seconds
      setTimeout(() => {
        setShowPsstNotification(false);
      }, 3000);
    };

    // Show first notification after 3 seconds
    const initialTimeout = setTimeout(showNotification, 3000);

    // Then show periodically every 30 seconds
    const interval = setInterval(showNotification, 30000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isOpen]);

  const quickActions = [
    "Find a DJ mentor",
    "Learn music production",
    "Improve my mixing skills"
  ];

  const handleQuickAction = (action: string) => {
    // Check cooldown
    const now = Date.now();
    if (now - lastMessageTime < COOLDOWN_MS) {
      const remainingSeconds = Math.ceil((COOLDOWN_MS - (now - lastMessageTime)) / 1000);
      toast({
        title: "Slow down there! 🎵",
        description: `Please wait ${remainingSeconds} seconds between messages`,
        variant: "default",
      });
      return;
    }

    // Check usage limits
    if (remainingMessages !== null && remainingMessages <= 0) {
      toast({
        title: "Daily limit reached 📊",
        description: "You've used all 10 messages today. Come back tomorrow for more!",
        variant: "default",
      });
      return;
    }

    setInput(action);
    setLastMessageTime(now);
    setCanSendMessage(false);
    setTimeout(() => setCanSendMessage(true), COOLDOWN_MS);

    // Auto-send the quick action
    setTimeout(() => {
      const userMessage: Message = { role: 'user', content: action };
      const updatedMessages = [...messages, userMessage];
      
      setMessages(updatedMessages);
      setInput("");
      setIsLoading(true);

      supabase.functions.invoke('ai-mentor-matchmaker', {
        body: { messages: updatedMessages }
      }).then(async ({ data, error }) => {
        if (error) {
          toast({
            title: "Error",
            description: error.message || 'Failed to get response from AI',
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        if (data?.response) {
          const newMessage: Message = { 
            role: 'assistant', 
            content: data.response 
          };
          
          // Store mentor IDs if available
          if (data?.mentorIds && Array.isArray(data.mentorIds)) {
            (newMessage as any).mentorIds = data.mentorIds;
          }
          
          setMessages(prev => [...prev, newMessage]);

          // Increment usage tracking
          const { data: usageData } = await supabase.rpc('increment_ai_chat_usage', {
            p_user_id: userId!
          });
          if (usageData !== null) {
            setRemainingMessages(DAILY_LIMIT_AUTH - usageData);
          }
        }
        setIsLoading(false);
      });
    }, 100);
  };

  const handleOpen = () => {
    setIsOpen(true);
    
    // Track chatbot engagement as a lead
    trackChatbotLead();
    
    // Send initial greeting if no messages
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hey! 👋 I'm Zeus, your AI mentor matchmaker.\n\nI can help you find the perfect mentor based on:\n• Your music genre and style\n• Your skill level\n• What you want to learn\n\nWhat brings you here today?"
      }]);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    // Check cooldown
    const now = Date.now();
    if (now - lastMessageTime < COOLDOWN_MS) {
      const remainingSeconds = Math.ceil((COOLDOWN_MS - (now - lastMessageTime)) / 1000);
      toast({
        title: "Slow down there! 🎵",
        description: `Please wait ${remainingSeconds} seconds between messages`,
        variant: "default",
      });
      return;
    }

    // Check usage limits
    if (remainingMessages !== null && remainingMessages <= 0) {
      toast({
        title: "Daily limit reached 📊",
        description: "You've used all 10 messages today. Come back tomorrow for more!",
        variant: "default",
      });
      return;
    }

    const userMessage: Message = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    
    setMessages(updatedMessages);
    setInput("");
    setIsLoading(true);
    setLastMessageTime(now);
    setCanSendMessage(false);

    // Start cooldown timer
    setTimeout(() => setCanSendMessage(true), COOLDOWN_MS);

    try {
      console.log('🤖 Zeus: Sending message to AI...', updatedMessages);
      
      // Call the edge function using Supabase client
      const { data, error } = await supabase.functions.invoke('ai-mentor-matchmaker', {
        body: { messages: updatedMessages }
      });

      console.log('🤖 Zeus: Response received:', { data, error });

      if (error) {
        console.error('❌ Zeus: Edge function error:', error);
        toast({
          title: "Error",
          description: error.message || 'Failed to get response from AI',
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      console.log('✅ Zeus: Got response:', data);

      // Add the AI message
      if (data?.response) {
        const newMessage: Message = { 
          role: 'assistant', 
          content: data.response 
        };
        
        // Store mentor IDs if available
        if (data?.mentorIds && Array.isArray(data.mentorIds)) {
          (newMessage as any).mentorIds = data.mentorIds;
        }
        
        setMessages(prev => [...prev, newMessage]);

        // Increment usage tracking
        const { data: usageData } = await supabase.rpc('increment_ai_chat_usage', {
          p_user_id: userId!
        });
        if (usageData !== null) {
          setRemainingMessages(DAILY_LIMIT_AUTH - usageData);
        }
      } else {
        throw new Error('No response from AI');
      }

      setIsLoading(false);

    } catch (error) {
      console.error('❌ Zeus: Chat error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to get response. Please try again.",
        variant: "destructive",
      });
      
      setIsLoading(false);
    }
  };

  // For unauthenticated users, show locked state
  if (!isAuthenticated) {
    return (
      <>
        {/* Floating Chat Bubble */}
        {!isOpen && (
          <div className="fixed bottom-6 right-6 z-50">
            {/* "Psst!" Notification */}
            {showPsstNotification && (
              <div className="absolute -top-10 right-7 animate-fade-in">
                <span className="text-primary font-bold text-sm" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 4px rgba(0, 0, 0, 0.6)' }}>
                  Psst!
                </span>
              </div>
            )}
            
            <button
              onClick={() => setIsOpen(true)}
              className="relative bg-gradient-to-br from-primary to-primary/80 text-primary-foreground px-4 py-3 rounded-xl shadow-lg energy-glow hover:scale-105 transition-transform cursor-pointer border-0"
              style={{
                borderBottomRightRadius: '4px',
              }}
            >
              <MessageSquare className="h-5 w-5" />
              {/* Speech bubble tail */}
              <div 
                className="absolute -bottom-1 right-0 w-2.5 h-2.5 bg-gradient-to-br from-primary to-primary/80"
                style={{
                  clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                }}
              />
            </button>
          </div>
        )}

        {/* Locked Chat Interface */}
        {isOpen && (
          <Card className="fixed bottom-4 right-4 left-4 md:left-auto md:bottom-6 md:right-6 md:w-[380px] md:h-[400px] flex flex-col shadow-2xl z-50 border-primary/20">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-primary/5">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Zeus</h3>
                  <p className="text-xs text-muted-foreground">Your AI mentor matchmaker</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Signup Prompt */}
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center space-y-4">
                <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
                  <MessageSquare className="h-8 w-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-lg">Hey! 👋</h4>
                  <p className="text-sm text-muted-foreground">
                    I'm Zeus, your AI mentor matchmaker. To chat with me and find your perfect mentor, please create a free account or sign in!
                  </p>
                </div>
                <div className="flex flex-col gap-2 pt-2">
                  <Button
                    onClick={() => {
                      trackChatbotLead();
                      window.location.href = '/auth/student';
                    }}
                    className="energy-button w-full"
                  >
                    Create Account
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/auth/student'}
                    variant="outline"
                    className="w-full"
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
      </>
    );
  }

  // For authenticated users, show full chat interface
  return (
    <>
      {/* Floating Chat Bubble */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          {/* "Psst!" Notification */}
          {showPsstNotification && (
            <div className="absolute -top-10 right-7 animate-fade-in">
              <span className="text-primary font-bold text-sm" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 4px rgba(0, 0, 0, 0.6)' }}>
                Psst!
              </span>
            </div>
          )}
          
          <button
            onClick={handleOpen}
            className="relative bg-gradient-to-br from-primary to-primary/80 text-primary-foreground px-4 py-3 rounded-xl shadow-lg energy-glow hover:scale-105 transition-transform cursor-pointer border-0"
            style={{
              borderBottomRightRadius: '4px',
            }}
          >
            <MessageSquare className="h-5 w-5" />
            {/* Speech bubble tail */}
            <div 
              className="absolute -bottom-1 right-0 w-2.5 h-2.5 bg-gradient-to-br from-primary to-primary/80"
              style={{
                clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
              }}
            />
          </button>
        </div>
      )}

      {/* Chat Interface */}
      {isOpen && (
        <Card className="fixed bottom-4 right-4 left-4 md:left-auto md:bottom-6 md:right-6 md:w-[380px] h-[calc(100vh-2rem)] md:h-[600px] flex flex-col shadow-2xl z-50 border-primary/20">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-primary/5">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                <MessageSquare className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Zeus</h3>
                <p className="text-xs text-muted-foreground">Your AI mentor matchmaker</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 shadow-sm ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  {/* Render message with interleaved booking buttons */}
                  {message.role === 'assistant' && 
                   isStudentDashboard && 
                   (message as any).mentorIds && 
                   Array.isArray((message as any).mentorIds) && 
                   (message as any).mentorIds.length > 0 ? (
                    <div className="space-y-3">
                      {(() => {
                        const mentorIds = (message as any).mentorIds as string[];
                        const content = message.content;
                        
                        // Split by emoji music notes (🎵) which marks each mentor
                        const sections = content.split('🎵').filter(s => s.trim());
                        
                        if (sections.length === 0) {
                          // Fallback if splitting didn't work
                          return (
                            <>
                              <p className="text-sm whitespace-pre-wrap">{content}</p>
                              {mentorIds.map((mentorId: string, idx: number) => (
                                <Button
                                  key={idx}
                                  size="sm"
                                  className="w-full energy-button text-xs"
                                  onClick={() => onBookMentor?.(mentorId)}
                                >
                                  Book This Mentor
                                </Button>
                              ))}
                            </>
                          );
                        }
                        
                        let mentorIndex = 0; // Track mentor index separately
                        return sections.map((section, idx) => {
                          // First section is intro if content doesn't start with 🎵
                          const isIntroSection = idx === 0 && !content.startsWith('🎵');
                          const isMentorSection = !isIntroSection;
                          
                          const currentMentorId = isMentorSection && mentorIndex < mentorIds.length 
                            ? mentorIds[mentorIndex] 
                            : null;
                          
                          if (isMentorSection) mentorIndex++;
                          
                          return (
                            <div key={idx}>
                              <p className="text-sm whitespace-pre-wrap">
                                {isMentorSection && '🎵'}{section.trim()}
                              </p>
                              {currentMentorId && (
                                <Button
                                  size="sm"
                                  className="w-full energy-button text-xs mt-2"
                                  onClick={() => {
                                    setIsOpen(false);
                                    onBookMentor?.(currentMentorId);
                                  }}
                                >
                                  Book This Mentor
                                </Button>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  )}
                </div>
              </div>
            ))}
            
            {/* Quick Actions - Show only at start */}
            {messages.length === 1 && !isLoading && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground text-center">Try asking:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {quickActions.map((action, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickAction(action)}
                      className="text-xs"
                    >
                      {action}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Typing Indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg p-3 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs text-muted-foreground">Zeus is thinking...</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border">
            {/* Usage Counter */}
            {remainingMessages !== null && (
              <div className="mb-3 flex items-center justify-between text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  {!canSendMessage && <Clock className="h-3 w-3 animate-pulse" />}
                  {canSendMessage ? 'Ready to send' : 'Cooldown active'}
                </span>
                <span className={`font-medium ${
                  remainingMessages <= 2 ? 'text-destructive' : 
                  remainingMessages <= 5 ? 'text-warning' : 
                  'text-primary'
                }`}>
                  {remainingMessages} {remainingMessages === 1 ? 'message' : 'messages'} left today
                </span>
              </div>
            )}
            
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder={remainingMessages === 0 ? "Daily limit reached..." : "Type your message..."}
                className="min-h-[60px] resize-none"
                disabled={isLoading || remainingMessages === 0 || !canSendMessage}
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading || remainingMessages === 0 || !canSendMessage}
                size="icon"
                className="h-[60px] w-[60px] shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Powered by AI • Press Enter to send • Resets daily at midnight UTC
            </p>
          </div>
        </Card>
      )}
    </>
  );
};
